import { useState, useEffect, useRef, useCallback } from "react";
import BudmenLogo from '../shared/BudmenLogo';

// ═══════════════════════════════════════════════════════════════════
// SHARED COMPONENTS FOR ALL REPORTS
// ═══════════════════════════════════════════════════════════════════

// Custom hook for scroll reveal animations
export function useReveal() {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(element);
        }
      },
      { threshold: 0.12 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return [ref, isVisible];
}

// Scroll reveal wrapper component
export function ScrollReveal({ children, className = "", delay = 0 }) {
  const [ref, isVisible] = useReveal();

  return (
    <div
      ref={ref}
      className={`fs rs ${isVisible ? "vis" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

// Label component for section headers
export function Label({ children, color }) {
  const colorClass = color === "var(--gold)" ? "report-label--gold" :
                     color === "var(--rust)" ? "report-label--rust" :
                     "report-label--teal";

  return (
    <div className={`report-label ${colorClass}`}>
      {children}
    </div>
  );
}

// Stat display component
export function Stat({ number, label }) {
  return (
    <div className="stat-item">
      <div className="stat-number">{number}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

// Divider component
export function Divider() {
  return <div className="report-divider" />;
}

// Photo placeholder component
export function Photo({ caption, aspect = "3/2" }) {
  return (
    <div className="photo-placeholder" style={{ aspectRatio: aspect }}>
      <div className="photo-placeholder__pattern" />
      <div className="photo-placeholder__content">
        <div className="photo-placeholder__icon">📷</div>
        <div className="photo-caption">{caption}</div>
      </div>
    </div>
  );
}

// Footer component
export function ReportFooter({ reportDate, partner }) {
  const [includePhotos, setIncludePhotos] = useState(true);

  const handlePrint = useCallback(() => {
    // Add or remove class to body based on checkbox
    if (!includePhotos) {
      document.body.classList.add('hide-photos-in-print');
    }

    // Trigger print
    window.print();

    // Clean up after print
    setTimeout(() => {
      document.body.classList.remove('hide-photos-in-print');
    }, 100);
  }, [includePhotos]);

  return (
    <div className="report-footer">
      <div className="report-footer__pattern" />
      <div className="report-footer__content">
        <div className="report-footer__logo">
          <BudmenLogo fill="white" height="30" />
        </div>
        <p className="report-footer__meta">
          Prepared {reportDate} · {partner}
          <br />
          Questions? hello@budmenindustries.com
        </p>
        <div className="no-print report-footer__print-controls">
          <label className="report-footer__checkbox-label">
            <input
              type="checkbox"
              checked={includePhotos}
              onChange={(e) => setIncludePhotos(e.target.checked)}
              className="report-footer__checkbox"
            />
            <span>Include photos in PDF</span>
          </label>
          <button
            className="report-footer__button"
            onClick={handlePrint}
          >
            ↓ Save as PDF
          </button>
        </div>
        <p className="report-footer__copyright">
          © {new Date().getFullYear()} Budmen Industries. All rights reserved.
        </p>
      </div>
    </div>
  );
}

// Max width container
export const containerClassName = "report-container-inner";